import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import ToggleMapButtonDesktop from '../../components/BasicUI/Button/ToggleMapButtonDesktop';
import SearchFiltersMobile from '../../components/Filters/SearchFiltersMobile/SearchFiltersMobile';
import SearchFiltersPrimary from '../../components/Filters/SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersSecondary from '../../components/Filters/SearchFiltersSecondary/SearchFiltersSecondary';
import SortBy from '../../components/Filters/SortBy/SortBy';
import SearchResultsPanel from '../../components/SearchResultsPanel/SearchResultsPanel';
import config from '../../config';
import { saveFilters } from '../../ducks/mainPanel.duck';
import TopbarSearchForm from '../../forms/TopbarSearchForm/TopbarSearchForm';
import routeConfiguration from '../../routeConfiguration';
import { deleteClientData, getClientData, setClientData } from '../../util/clientData';
import usePass from '../../util/hooks/usePass';
import useScreenSize from '../../util/hooks/useScreenSize';
import { isCordova } from '../../util/mobile/deviceDetection';
import { createResourceLocatorString } from '../../util/routes';
import { isAnyFilterActive } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import FilterComponent from './FilterComponent';
import { validFilterParams } from './SearchPage.helpers';

const SAVE_FILTERS_KEY = 'vansite.savedFilters';

const isEqual = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((val, index) => val === b[index]);
  }
  return a === b;
};

const cleanSearchFromConflictingParams = (searchParams, sortConfig, filterConfig) => {
  // Single out filters that should disable SortBy when an active
  // keyword search sorts the listings according to relevance.
  // In those cases, sort parameter should be removed.
  const sortingFiltersActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    searchParams,
    filterConfig,
  );
  return sortingFiltersActive
    ? { ...searchParams, [sortConfig.queryParamName]: null }
    : searchParams;
};

export const getFilterConfig = (config, annualPassIsValid) => {
  const filterConfig = JSON.parse(JSON.stringify(config));
  if (annualPassIsValid && filterConfig.id === 'responseTime') return null;
  if (filterConfig.id === 'responseTime') return null;
  if (filterConfig.id === 'cancellationPolicy') return null;
  if (filterConfig.id === 'bookingLimit') return null;
  if (filterConfig.id === 'category') {
    filterConfig.config.options = filterConfig.config.options.filter((option) => {
      return option.key !== 'bookingRequest';
    });
  }
  return filterConfig;
};

/**
 * MainPanel contains search results and filters.
 * There are 3 presentational container-components that show filters:
 * SearchfiltersMobile, SearchFiltersPrimary, and SearchFiltersSecondary.
 * The last 2 are for desktop layout.
 */
const MainPanelComponent = ({
  urlQueryParams,
  listings = [],
  searchInProgress = false,
  searchListingsError = false,
  searchParamsAreInSync,
  searchParamsForPagination = {},
  paginationPageName = 'SearchPage',
  paginationPathParams = {},
  onActivateListing,
  onManageDisableScrolling,
  onMapIconClick,
  onListIconClick,
  pagination = null,
  showAsModalMaxWidth,
  filterConfig = config.custom.filters,
  sortConfig = config.custom.sortConfig,
  amountListings = 100,
  shouldShowSearchMap = true,
  isStoryblok = false,
  urlCategory = '',
  isLandingPage = false,
  inSearchPage = false,
  disableHoverOnListings = false,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [annualPassIsValid] = usePass();
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(null);

  // Conditionally set the active listing function
  const setActiveListing = useCallback((id) => {
    if (disableHoverOnListings) {
      return;
    }
    onActivateListing(id);
  }, [disableHoverOnListings, onActivateListing]);

  // Calculate panel size styles based on screen size and map visibility
  const panelSizesStyle = useMemo(
    () => ({
      width: isMobile
        ? '100%'
        : isTablet
          ? shouldShowSearchMap
            ? '50%'
            : '100%'
          : shouldShowSearchMap
            ? '62%'
            : '100%',
      px: (isLandingPage && isDesktop) || (isStoryblok && isMobile) ? 0 : 2,
    }),
    [isMobile, isTablet, shouldShowSearchMap],
  );

  // State to manage secondary filters open state & hold the current query parameters
  const [isSecondaryFiltersOpenState, setIsSecondaryFiltersOpenState] = useState(false);
  const [currentQueryParams, setCurrentQueryParams] = useState(urlQueryParams);

  /**
   * Filter out empty parameters and clean conflicting search params.
   * This function removes parameters that are undefined, null, empty strings,
   * or empty arrays, and then cleans any conflicting search parameters.
   */
  const filterAndCleanSearchParams = useCallback(
    (params) => {
      // Remove empty parameters
      const cleanedParams = omitBy(
        params,
        (value) =>
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0),
      );

      // Clean conflicting search parameters
      return cleanSearchFromConflictingParams(cleanedParams, sortConfig, filterConfig);
    },
    [filterConfig, sortConfig],
  );

  /**
   * Apply saved filters from localStorage on component mount and listen for navigation events.
   */
  useEffect(() => {
    const applySavedFilters = () => {
      // Don't apply saved filters on landing page or storyblok pages
      // Storyblok have pre defined filters, so we don't want to override them
      if (!isLandingPage) {
        const savedFilters = isCordova() && getClientData(SAVE_FILTERS_KEY);

        if (savedFilters) {
          const parsedFilters = JSON.parse(savedFilters);
          const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search));

          // Check if all saved filters are present in the current URL new keys and new values in key !
          const savedFiltersInUrl = Object.keys(parsedFilters).every(
            (key) =>
              currentUrlParams.hasOwnProperty(key) &&
              isEqual(currentUrlParams[key], parsedFilters[key]),
          );

          // If saved filters are not already in URL, merge them and update the URL
          if (!savedFiltersInUrl) {
            const mergedParams = filterAndCleanSearchParams({
              ...currentUrlParams,
              ...parsedFilters,
            });

            setCurrentQueryParams(mergedParams);
            const searchParams = new URLSearchParams(mergedParams).toString();

            if (location.search !== `?${searchParams}`) {
              navigate(`?${searchParams}`, { replace: true });
            }
          }
        }
      }
    };

    applySavedFilters();
  }, [location.search, isLandingPage, isStoryblok, filterAndCleanSearchParams, navigate]);

  /**
   * Cancel filters by resetting current query parameters
   * @type {(function(): void)|*}
   */
  const cancelFilters = () => {
    setCurrentQueryParams({});
  };

  /**
   * Reset all filters, query parameters and localStorage saved filters.
   * @type {(function(): void)|*}
   */
  const resetAll = useCallback(() => {
    // Get the query parameter names of all filters
    const filterQueryParamNames = filterConfig.map((f) => f.queryParamNames);

    // Clear current query parameters and reset localStorage filters
    setCurrentQueryParams({});
    if (isCordova()) deleteClientData(SAVE_FILTERS_KEY); // Clear saved filters from localStorage

    // Reset state
    setCurrentQueryParams({});

    // Remove filter query parameters from URL query parameters
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    navigate(
      createResourceLocatorString('SearchPage', routeConfiguration(), {}, queryParams, intl.locale),
    );
  }, [navigate, urlQueryParams, filterConfig, intl.locale]);

  /**
   * Get initial values for filters.
   * @type {function(*): *|{}}
   */
  const initialValues = useCallback(
    (queryParamNames) => {
      // Get initial value for a given parameter from state if its there.
      const getInitialValue = (paramName) => {
        const currentQueryParam = currentQueryParams[paramName];
        const hasQueryParamInState = currentQueryParam !== undefined;
        return hasQueryParamInState ? currentQueryParam : urlQueryParams[paramName];
      };

      // Return all the initial values related to given queryParamNames
      // InitialValues for "amenities" filter could be
      // { amenities: "has_any:wc,electricity" }
      const isArray = Array.isArray(queryParamNames);
      return isArray
        ? queryParamNames.reduce((acc, paramName) => {
          return { ...acc, [paramName]: getInitialValue(paramName) };
        }, {})
        : {};
    },
    [urlQueryParams, currentQueryParams],
  );

  // Ref to check if it's the first update
  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    const searchParams = currentQueryParams;

    // If 'distance' filter is active, remove 'sort' to prevent conflicts
    if ('distance' in searchParams) {
      if ('sort' in searchParams) {
        delete searchParams.sort;
      }
    }
  }, [urlQueryParams, currentQueryParams]);

  /**
   * Get a function that handles changed value for a filter.
   * @type {function(*): function(*): void}
   */
  const getHandleChangedValueFn = useCallback(
    (useHistoryPush) => {
      return (updatedURLParams) => {
        const { address, bounds } = urlQueryParams;
        // URL params are the source of truth when navigating immediately; compute the
        // navigate payload up-front so it doesn't depend on React running the setState
        // updater synchronously (it may not in React 18).
        const newSearchParams = { ...urlQueryParams, ...updatedURLParams, address, bounds };

        setCurrentQueryParams((prevState) => {
          const mergedQueryParams = { ...prevState, ...urlQueryParams };
          return { ...mergedQueryParams, ...updatedURLParams, address, bounds };
        });

        if (useHistoryPush) {
          const search = cleanSearchFromConflictingParams(newSearchParams, sortConfig, filterConfig);
          const url = createResourceLocatorString(
            'SearchPage',
            routeConfiguration(),
            {},
            search,
            intl.locale,
          );

          const filteredSearch = filterAndCleanSearchParams(newSearchParams);
          const filtersToSave = omit(filteredSearch, ['tab', 'address', 'bounds']);
          if (isCordova())
            setClientData(
              SAVE_FILTERS_KEY,
              JSON.stringify(filtersToSave),
              moment().add(100, 'years'),
            );
          dispatch(saveFilters(filtersToSave));

          navigate(url, { replace: true });
        }
      };
    },
    [sortConfig, filterConfig, intl.locale, navigate, dispatch, urlQueryParams, filterAndCleanSearchParams],
  );

  /**
   * Handle sort by button, which orderes the listings.
   * @type {(function(*, *): void)|*}
   */
  const handleSortBy = useCallback(
    (urlParam, values) => {
      const queryParams = values
        ? { ...urlQueryParams, [urlParam]: values }
        : omit(urlQueryParams, urlParam);

      // If 'distance' is in queryParams, remove it when sorting
      if ('distance' in queryParams) {
        delete queryParams.distance;
        setCurrentQueryParams(queryParams);
      }

      // Update the URL with the new sort parameter
      navigate(
        createResourceLocatorString(
          'SearchPage',
          routeConfiguration(),
          {},
          queryParams,
          intl.locale,
        ),
      );
    },
    [navigate, urlQueryParams, intl.locale],
  );

  /**
   * Function to sort listings by distance
   * @param direction
   * @param listings
   */
  const sortListingsForDistance = (direction, listings) => {
    if (direction === 'up') {
      // Sort listings in ascending order of distance
      listings.sort((a, b) => {
        if (parseInt(a.attributes.distance) < parseInt(b.attributes.distance)) {
          return -1;
        }
        if (parseInt(a.attributes.distance) > parseInt(b.attributes.distance)) {
          return 1;
        }
        return 0;
      });
    } else {
      // Sort listings in descending order of distance
      listings.sort((a, b) => {
        if (parseInt(a.attributes.distance) > parseInt(b.attributes.distance)) {
          return -1;
        }
        if (parseInt(a.attributes.distance) < parseInt(b.attributes.distance)) {
          return 1;
        }
        return 0;
      });
    }
  };

  // Separate primary and secondary filters based on group
  const primaryFilters = filterConfig.filter((f) => f.group === 'primary');
  const secondaryFilters = filterConfig.filter((f) => f.group !== 'primary');
  const hasSecondaryFilters = !!(secondaryFilters && secondaryFilters.length > 0);

  // Selected (active) filters
  const selectedFilters = validFilterParams(urlQueryParams, filterConfig);
  const selectedFiltersCount = Object.values(selectedFilters)
    .map((value) => value.split(','))
    .flat().length;

  // Selected (active) secondary filters
  const selectedSecondaryFilters = hasSecondaryFilters
    ? validFilterParams(urlQueryParams, secondaryFilters)
    : {};
  const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

  // Determine if secondary filters panel is open
  const isSecondaryFiltersOpen = !!hasSecondaryFilters && isSecondaryFiltersOpenState;
  const propsForSecondaryFiltersToggle = hasSecondaryFilters
    ? {
      isSecondaryFiltersOpen: isSecondaryFiltersOpenState,
      toggleSecondaryFiltersOpen: (isOpen) => {
        setIsSecondaryFiltersOpenState(isOpen);
        setIsOpen(null);
      },
      selectedSecondaryFiltersCount,
    }
    : {};

  // Check if we have pagination info
  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  const totalItems = searchParamsAreInSync && hasPaginationInfo ? pagination.totalItems : 0;
  const perPage = pagination ? pagination.perPage : 0;
  const listingsAreLoaded = !searchInProgress && searchParamsAreInSync && hasPaginationInfo;

  const mapToggleButton = (
    <ToggleMapButtonDesktop
      isLandingPage={isLandingPage}
      onListIconClick={() => {
        onListIconClick();
        setIsOpen(null);
        setIsSecondaryFiltersOpenState(false);
      }}
      onMapIconClick={() => {
        onMapIconClick();
        setIsOpen(null);
        setIsSecondaryFiltersOpenState(false);
      }}
      shouldShowSearchMap={shouldShowSearchMap}
    />
  );

  const sortBy = () => {
    // Check if any conflicting filters are active
    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      urlQueryParams,
      filterConfig,
    );

    return sortConfig.active ? (
      <SortBy
        annualPassIsValid={annualPassIsValid}
        isConflictingFilterActive={!!conflictingFilterActive}
        isMobile={isMobile}
        onSelect={handleSortBy}
        showAsPopup
        sort={urlQueryParams[sortConfig.queryParamName]}
      />
    ) : null;
  };

  // If 'sort' is in urlQueryParams, remove 'distance' parameter
  if ('sort' in urlQueryParams) {
    delete urlQueryParams.distance;
  }
  // If 'distance' is in urlQueryParams, sort listings by distance
  if ('distance' in urlQueryParams) {
    sortListingsForDistance(urlQueryParams.distance, listings);
  }

  const searchFiltersMobile = (
    <SearchFiltersMobile
      isLandingPage={isLandingPage}
      isStoryblok={isStoryblok}
      listingsAreLoaded={listingsAreLoaded}
      onManageDisableScrolling={onManageDisableScrolling}
      onMapIconClick={onMapIconClick}
      perPage={perPage}
      resetAll={resetAll}
      resultsCount={totalItems}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      selectedFiltersCount={selectedFiltersCount}
      shouldShowSearchMap={shouldShowSearchMap}
      showAsModalMaxWidth={showAsModalMaxWidth}
      sortByComponent={sortBy()}
      urlQueryParams={urlQueryParams}
    >
      {filterConfig.map((config) => {
        const filterConfig = getFilterConfig(config, annualPassIsValid);
        return (
          <FilterComponent
            filterConfig={filterConfig || config}
            getHandleChangedValueFn={getHandleChangedValueFn}
            idPrefix="SearchFiltersMobile"
            initialValues={initialValues}
            key={`SearchFiltersMobile.${filterConfig?.id || config.id}`}
            liveEdit
            showAsPopup={false}
          />
        );
      })}
    </SearchFiltersMobile>
  );

  const searchFiltersDesktop = (
    <SearchFiltersPrimary
      listingsAreLoaded={listingsAreLoaded}
      mapToggleComponent={mapToggleButton}
      perPage={perPage}
      resultsCount={totalItems}
      sortByComponent={sortBy()}
      {...propsForSecondaryFiltersToggle}
    >
      {[...primaryFilters.entries()].map(([index, config]) => {
        if (annualPassIsValid && config.id === 'price') return null;
        return (
          <FilterComponent
            filterConfig={config}
            getHandleChangedValueFn={getHandleChangedValueFn}
            idPrefix="SearchFiltersPrimary"
            initialValues={initialValues}
            isOpen={isOpen === index}
            key={`SearchFiltersPrimary.${config.id}`}
            setIsOpen={() => setIsOpen((prevState) => (prevState === index ? null : index))}
            showAsPopup
          />
        );
      })}
    </SearchFiltersPrimary>
  );

  // Inside MainPanelComponent

  const applySecondaryFilters = useCallback(() => {
    // Clean the current query parameters
    const cleanedParams = filterAndCleanSearchParams(currentQueryParams);

    // Construct the search string for the URL
    //const search = new URLSearchParams(cleanedParams).toString();
    const url = createResourceLocatorString(
      'SearchPage',
      routeConfiguration(),
      {},
      cleanedParams,
      intl.locale,
    );

    // Prepare filters to save (excluding certain keys)
    const filtersToSave = omit(cleanedParams, ['tab', 'address', 'bounds']);

    // Save filters to local storage if in Cordova environment
    if (isCordova()) {
      setClientData(SAVE_FILTERS_KEY, JSON.stringify(filtersToSave), moment().add(100, 'years'));
    }

    // Dispatch Redux action to save filters
    dispatch(saveFilters(filtersToSave));

    // Update the browser's URL without adding a new entry to the history stack
    navigate(url, { replace: true });

    // Close the secondary filters panel
    setIsSecondaryFiltersOpenState(false);
  }, [
    filterAndCleanSearchParams,
    currentQueryParams,
    createResourceLocatorString,
    routeConfiguration,
    intl.locale,
    isCordova,
    SAVE_FILTERS_KEY,
    setClientData,
    dispatch,
    saveFilters,
    navigate,
  ]);

  const secondaryFiltersBox = (
    <SearchFiltersSecondary
      applyFilters={applySecondaryFilters}
      cancelFilters={cancelFilters}
      listingsAreLoaded={listingsAreLoaded}
      onClosePanel={() => setIsSecondaryFiltersOpenState(false)}
      resetAll={resetAll}
      {...propsForSecondaryFiltersToggle}
    >
      {secondaryFilters.map((config) => {
        const filterConfig = getFilterConfig(config, annualPassIsValid);
        return (
          <FilterComponent
            filterConfig={filterConfig || config}
            getHandleChangedValueFn={getHandleChangedValueFn}
            idPrefix="SearchFiltersSecondary"
            initialValues={initialValues}
            isMobileLayout={isMobile}
            key={`SearchFiltersSecondary.${filterConfig?.id || config.id}`}
            showAsPopup={false}
            urlQueryParams={urlQueryParams}
          />
        );
      })}
    </SearchFiltersSecondary>
  );

  const showSearchResults = (isMobile && !shouldShowSearchMap) || isTablet || isDesktop;

  const searchResultsListings = showSearchResults ? (
    <Stack spacing={2}>
      {searchListingsError ? (
        <Typography variant="h2">
          <FormattedMessage id="SearchPage.searchError" />
        </Typography>
      ) : null}

      <SearchResultsPanel
        amountListings={amountListings}
        listings={listings}
        onMapIconClick={onMapIconClick}
        pagination={listingsAreLoaded ? pagination : null}
        paginationPageName={paginationPageName}
        paginationPathParams={paginationPathParams}
        search={searchParamsForPagination}
        setActiveListing={setActiveListing}
        shouldShowSearchMap={shouldShowSearchMap}
        urlCategory={urlCategory}
      />
    </Stack>
  ) : null;

  const currentSearchParams = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const handleSubmit = ({ location }) => {
    const { search, selectedPlace } = location;
    const { origin, bounds } = selectedPlace;
    const originMaybe = config.sortSearchByDistance ? { origin } : {};
    const searchParams = {
      ...currentSearchParams,
      ...originMaybe,
      address: search,
      bounds,
    };
    navigate(
      createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams, intl.locale),
    );
  };

  const { address, origin, bounds } = currentSearchParams;

  // Only render current search if full place object is available in the URL params
  const locationFieldsPresent = config.sortSearchByDistance
    ? address && origin && bounds
    : address && bounds;
  const initialSearchFormValues = {
    location: locationFieldsPresent
      ? {
        search: address,
        selectedPlace: { address, origin, bounds },
      }
      : null,
  };

  const searchBar = (
    <Box
      data-testid="searchpage-search"
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 10,
        boxShadow: 2,
        px: 2,
        py: 1.5,
        my: 1,
      }}
    >
      <TopbarSearchForm inSearchPage={inSearchPage} initialValues={initialSearchFormValues} onSubmit={handleSubmit} />
    </Box>
  );

  return (
    <Box sx={panelSizesStyle}>
      {!isMobile && !isStoryblok ? searchBar : null}
      {isMobile ? searchFiltersMobile : searchFiltersDesktop}
      {isSecondaryFiltersOpen ? secondaryFiltersBox : searchResultsListings}
    </Box>
  );
};

MainPanelComponent.displayName = 'MainPanel';
const MainPanel = memo(MainPanelComponent);
export default MainPanel;
